// The landing page.
//
// The name of the game and a sign-up form. No tagline beyond the one line that
// says what this is, no screenshot, no player count, no controls, no footer —
// it is the one screen a request to "add a bit about the game" must not touch.

import type { VNode } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { requestSignIn } from "../api";
import { SignUpGate } from "../turnstile";

/** The half of the Turnstile gate this page uses. Named so a test can hand the
 *  form a gate that always passes, always fails, or never answers, without a
 *  network or an iframe. */
export interface Gate {
  solve(): Promise<string>;
  clear(): Promise<void>;
}

const SIGN_IN_ERRORS: Readonly<Record<string, string>> = {
  "rate-limited": "too many links sent. try again later.",
  "bad-email": "that does not look like an email address.",
  "disposable-email": "throwaway inboxes are not allowed. use an address you keep.",
  "undeliverable-email": "that address cannot receive mail. check the spelling.",
  "captcha-failed": "the human check did not pass. try again.",
  maintenance: "the game is updating. try again in a few minutes.",
};

export interface LandingProps {
  siteKey: string;
  /** Injected by tests. In the browser this is Cloudflare's widget. */
  makeGate?: (host: HTMLElement) => Gate;
}

export function Landing({ siteKey, makeGate }: LandingProps): VNode {
  const host = useRef<HTMLDivElement>(null);
  const email = useRef<HTMLInputElement>(null);
  const gate = useRef<Gate | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => email.current?.focus(), []);

  /** Pressing Sign up runs the challenge first and the email second.
   *
   *  The player sees one state, "sending…", because the widget is invisible:
   *  there is nothing to click and nothing to look at. Nothing is posted until
   *  Turnstile hands back a token, so a widget that will not load and a
   *  challenge that is failed both end the same way, with no request made. The
   *  server checks the token again anyway — this half only decides what the
   *  player sees. */
  async function submit(ev: Event): Promise<void> {
    ev.preventDefault();
    const address = email.current?.value.trim() ?? "";
    if (!address || busy) return;
    setBusy(true);

    const build = makeGate ?? ((el: HTMLElement): Gate => new SignUpGate(el, siteKey));
    const widget = (gate.current ??= build(host.current as HTMLElement));

    let token: string;
    setNote("sending…");
    try {
      token = await widget.solve();
    } catch {
      setNote("we could not verify your browser. reload and try again.");
      setBusy(false);
      await widget.clear().catch(() => undefined);
      return;
    }

    const result = await requestSignIn(address, token);
    // The token is spent either way, so the widget goes back in its box and the
    // next press earns a fresh one.
    await widget.clear().catch(() => undefined);
    setBusy(false);
    if (!result.ok) {
      setNote(SIGN_IN_ERRORS[result.error] ?? "that did not work. check the address.");
      return;
    }
    // In local development the server hands the magic link straight back, so
    // signing in is one click. In production this is always undefined and the
    // player goes to their inbox.
    if (result.devUrl) {
      location.href = result.devUrl;
      return;
    }
    setNote("check your email for a sign-in link.");
  }

  return (
    <main class="landing">
      <div class="landing-inner">
        <h1>Mind the Fen</h1>
        <p class="tagline">
          An autonomous, community-developed game experiment. <a href="/about.html">Read more</a>
        </p>
        <form class="signin" onSubmit={(ev) => void submit(ev)}>
          <label class="visually-hidden" for="email">
            Email address
          </label>
          <input
            id="email"
            ref={email}
            type="email"
            name="email"
            placeholder="you@example.com"
            autocomplete="email"
            aria-describedby="landing-note"
            required
          />
          <button type="submit" disabled={busy}>
            Sign up
          </button>
        </form>
        {/* Cloudflare mounts its iframe in here and, because the sitekey is an
            Invisible one, draws nothing — the box stays empty and takes no
            room. It is not hidden: a display:none container is the documented
            way to stop Turnstile running at all, and a gate that does not run
            is a gate that does not gate. The empty innerHTML is not a value —
            it is how a Preact node is marked "somebody else owns the inside of
            this", so a re-render does not tear the widget out. */}
        <div class="captcha" ref={host} dangerouslySetInnerHTML={{ __html: "" }} />
        <p id="landing-note" class="landing-note" role="status">
          {note}
        </p>
      </div>
    </main>
  );
}
